// ── Jurisdiktions-Regeln (Steuer-Profil pro Land) ───────────────────────────
//
// Single Source of Truth dafür, WIE ein Land behandelt wird. Ersetzt die
// verstreuten `isDE`/`country === 'DE'`-Checks durch ein zentrales Profil.
//
// WICHTIG: reine ES-Module-Datei — KEINE Browser-/Firebase-/window-Deps.
// Wird sowohl vom Client (Screens, Contexts) als auch vom Server
// (api/_lib/pdfBuilder.js, excelBuilder.js) importiert. Gleicher Purity-Vertrag
// wie exportData.js / reportEngine.js.
//
// Profil-Felder:
//   costBasis             'fifo' | 'average'  — Verbrauchsfolge-/Bewertungsverfahren
//   swapTaxable           Krypto→Krypto-Tausch ist ein Realisierungs-Event?
//   holdingPeriodApplies  gibt es eine Haltefrist (nach X Tagen außerhalb)?
//   holdingPeriodDays     Länge der Haltefrist (nur wenn holdingPeriodApplies)
//   altvermoegenCutoff    ISO-Datum: Käufe DAVOR folgen Alt-Regel (1-Jahr-Frist),
//                         Käufe AB DANN sind Neuvermögen (stets als Realisierung
//                         gewertet). null = kein Alt/Neu-Split.
//   altvermoegenHoldingDays  Haltefrist für Altvermögen (nur AT)
//   rate                  Modell-Abzug für die UNVERBINDLICHE Schätzung (0 = keine)
//   lossCarryforward      Verlustvortrag in Folgejahre erlaubt?
//   showSmartCheck        Haltefrist-Feature ("Smart Check") sichtbar?
//   reportMode            'tax' = Bewertung im Bericht; 'log' = reines
//                         Anschaffungs-/Veräußerungsprotokoll ohne Bewertung.

// Deutschland: FIFO + §23-Spekulationsfrist (1 Jahr) + Smart Check. Unverändert.
const DE = Object.freeze({
  code:                 'DE',
  costBasis:            'fifo',
  swapTaxable:          true,
  holdingPeriodApplies: true,
  holdingPeriodDays:    365,
  altvermoegenCutoff:   null,
  altvermoegenHoldingDays: null,
  rate:                 0.26,
  lossCarryforward:     true,
  showSmartCheck:       true,
  reportMode:           'tax',
});

// Österreich (BMF, Neuvermögen ab 1.3.2021):
//   - 27,5 % KESt, KEINE Haltefrist für Neuvermögen.
//   - Krypto→Krypto-Tausch (inkl. Stablecoins) = KEINE Realisierung, Buchwert-
//     fortführung (Kostenbasis wandert auf den erhaltenen Coin).
//   - Realisierung nur bei: Krypto→Fiat, Bezahlen mit Krypto. Staking/Mining-
//     Einkünfte werden zum Tarif separat erfasst (NICHT Teil der 27,5 %-Schätzung).
//   - Kostenbasis = gleitender Durchschnittspreis je Coin.
//   - Verlustverrechnung nur im selben Kalenderjahr, KEIN Vortrag (Privat).
//   - Altvermögen (Kauf vor 1.3.2021): alte Regel, 1-Jahres-Frist.
const AT = Object.freeze({
  code:                 'AT',
  costBasis:            'average',
  swapTaxable:          false,
  holdingPeriodApplies: false,
  holdingPeriodDays:    null,
  altvermoegenCutoff:   '2021-03-01',
  altvermoegenHoldingDays: 365,
  rate:                 0.275,
  lossCarryforward:     false,
  showSmartCheck:       false,
  reportMode:           'tax',
});

// Alle anderen Länder: keine länderspezifische Bewertung. Berichte zeigen ein
// reines Protokoll (wann eine Tranche ge-/verkauft wurde), keine Bewertung,
// keine Schätzung. Dashboard/Tracking bleibt voll funktionsfähig.
const DEFAULT = Object.freeze({
  code:                 'OTHER',
  costBasis:            'fifo',
  swapTaxable:          true,
  holdingPeriodApplies: false,
  holdingPeriodDays:    null,
  altvermoegenCutoff:   null,
  altvermoegenHoldingDays: null,
  rate:                 0,
  lossCarryforward:     false,
  showSmartCheck:       false,
  reportMode:           'log',
});

const PROFILES = { DE, AT };

/**
 * Liefert das Profil für einen Ländercode (ISO-2). Unbekannt/leer → DEFAULT.
 * @param {string} country z.B. 'DE', 'AT', 'CH'
 * @returns {Readonly<object>} eingefrorenes Profil
 */
export function rulesFor(country) {
  const c = String(country || '').toUpperCase();
  return PROFILES[c] || DEFAULT;
}

/**
 * true, wenn das Land eine Bewertung im Bericht bekommt (DE/AT),
 * false für reine Protokoll-Länder.
 */
export function isTaxJurisdiction(country) {
  return rulesFor(country).reportMode === 'tax';
}

export { DE, AT, DEFAULT };
